import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { DashboardCard } from "@tellescope/ui";
import { getCurrencySymbol, formatDate } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

const purchaseIsInPast = (purchase: TellescopeModelFromName<"purchases">): boolean => {
  if (!purchase.processedAt) return false;
  return new Date(purchase.processedAt).getTime() < Date.now();
};

interface PaymentSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const PaymentSection = ({ contact }: PaymentSectionProps) => {
  const { t } = useTranslation("payments");
  const purchasesQuery = useTellescopeInfiniteQuery({
    model: "purchases",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  if (purchasesQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading purchases...")}</div>
        </div>
      </div>
    );
  }

  if (!purchasesQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No purchases found")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto space-y-4 pr-2">
      {purchasesQuery.data?.map((purchase) => (
        <DashboardCard
          key={purchase.id}
          title={purchase.title}
          eventDate={purchase.processedAt || undefined}
          dueDate={purchase.processedAt ? formatDate(purchase.processedAt) : ""}
          timestamp={purchase.processedAt || undefined}
          status={purchaseIsInPast(purchase) ? "default" : "outline"}
          statusLabel={purchaseIsInPast(purchase) ? "Complete" : "Incomplete"}
          assignees={[]}
          showUnreadDot={false}
          subtitle={`${getCurrencySymbol(purchase.cost.currency)}${purchase.cost.amount.toFixed(2)}`}
          subtitleSecondary={purchase.description}
          priority=""
        />
      ))}
    </div>
  );
};
